// FilteredEmptyState — "filters exclude everything" (§7 States, README "Empty").
//
// Translated from prototype/screens-states.jsx `Empty` (filter icon variant).
// Deliberately a SEPARATE component from EmptyState: the handoff requires
// distinguishing "nothing in this queue" from "filters exclude everything"
// because the fix is different (clear filters vs. go create/find work). It
// does not read FilterState itself — the caller already knows which filters
// are active and passes a rendered summary plus a clear-filters handler.
import React from 'react';
import { Box, Typography } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import HdButton from '../HdButton';

interface FilteredEmptyStateProps {
  /** e.g. "No tickets match these filters". */
  title?: string;
  /**
   * Plain-language summary of the active filters, e.g. "Status is Resolved
   * and Priority is Urgent on the IT Support desk." Built by the caller from
   * its own FilterState — this component has no knowledge of filter shape.
   */
  body: string;
  /**
   * Count of tickets the active filters are hiding, when the caller already
   * has it from data fetched for another purpose (e.g. queueCounts). Never
   * fetched here — omit rather than triggering a call.
   */
  hiddenCount?: number;
  /** "Clear filters" — required; this state exists to offer exactly that. */
  onClearFilters: () => void;
  /** Optional secondary action, e.g. "Search all desks". */
  action?: React.ReactNode;
}

export default function FilteredEmptyState({
  title = 'No tickets match these filters',
  body,
  hiddenCount,
  onClearFilters,
  action
}: FilteredEmptyStateProps) {
  return (
    <Box
      sx={{
        padding: '40px 28px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        textAlign: 'center'
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 'var(--r-md)',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: 'var(--card-sub)',
          color: 'var(--muted)',
          border: '1px solid var(--hairline)'
        }}
      >
        <FilterAltOutlinedIcon sx={{ fontSize: 21 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', marginTop: '5px', lineHeight: 1.6, maxWidth: 340 }}>
          {body}
          {typeof hiddenCount === 'number' && hiddenCount > 0 ? (
            <Box
              component="span"
              sx={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)', fontWeight: 600 }}
            >
              {` (${hiddenCount} hidden)`}
            </Box>
          ) : null}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
        <HdButton onClick={onClearFilters}>Clear filters</HdButton>
        {action}
      </Box>
    </Box>
  );
}
